import React from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Camera, Users, Sparkles } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { SITE, PHOTOS } from "@/data/content";
import { useAdminContent } from "@/lib/adminContent";

const HERO_SLIDES = ["/gallery/hero-bg-1.webp", "/gallery/hero-bg-2.webp"];

/**
 * Render order: brand (white) + suffix (accent, next to MAX) + optional tail (" Navodari", …).
 * If admin title does not start with "{brand} {suffix}", show full title as plain text.
 */
function heroTitleSegments(adminTitle, brand, suffix) {
  const t = (adminTitle || "").trim();
  const prefix = `${brand} ${suffix}`;

  if (!t) {
    return { mode: "parts", brand, suffix, tail: "" };
  }

  const lower = t.toLowerCase();
  const prefixLower = prefix.toLowerCase();
  if (lower.startsWith(prefixLower)) {
    const tail = t.slice(prefix.length).trim();
    return { mode: "parts", brand, suffix, tail };
  }

  return { mode: "plain", full: t };
}

/** Prefer explicit admin fields (titleLead / titleAccent / titleTail); else parse single title. */
function resolveHeroHeading(content, brand, suffix) {
  const lead = (content.titleLead || "").trim();
  const accent = (content.titleAccent || "").trim();
  const tail = (content.titleTail || "").trim();
  if (lead || accent || tail) {
    return {
      mode: "parts",
      brand: lead || brand,
      suffix: accent || suffix,
      tail: tail.trim(),
    };
  }
  return heroTitleSegments(content.title, brand, suffix);
}

const Hero = ({ onOpenOrder, onOpenTrainers }) => {
  const { t, lang } = useI18n();
  const { hero: heroContent } = useAdminContent(lang);
  const adminHeroBg = (heroContent?.backgroundImage || "").trim();
  const heroBackground = adminHeroBg || HERO_SLIDES[0];
  const prefersReducedMotion = useReducedMotion();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 140]);
  const opacity = useTransform(scrollY, [0, 500], [1, 0.2]);
  const heroMotionStyle = prefersReducedMotion ? undefined : { y, opacity };
  const heroHeading = resolveHeroHeading(heroContent, SITE.brand, SITE.brandSuffix);

  return (
    <section
      id="home"
      className="relative min-h-[100svh] flex items-end overflow-hidden pt-28 sm:pt-32"
      data-testid="hero-section"
    >
      <motion.div style={heroMotionStyle} className="absolute inset-0 z-0" aria-hidden>
        <motion.img
          key={heroBackground}
          src={heroBackground}
          srcSet={undefined}
          sizes="100vw"
          alt=""
          width="1024"
          height="682"
          decoding="async"
          fetchPriority="high"
          className="hero-image absolute inset-0 w-full h-full object-cover"
          animate={prefersReducedMotion ? { opacity: 1, scale: 1 } : { opacity: 1, scale: [1, 1.04, 1] }}
          transition={prefersReducedMotion ? { duration: 0 } : { scale: { duration: 24, repeat: Infinity, ease: "easeInOut" } }}
          onError={(e) => {
            e.currentTarget.src = PHOTOS.hero;
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, var(--hero-overlay-from) 0%, var(--hero-overlay-mid) 60%, var(--hero-overlay-to) 100%)",
          }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(90deg, var(--hero-overlay-mid) 0%, transparent 50%, var(--hero-overlay-from) 100%)",
          }}
        />
      </motion.div>

      <div className="absolute top-1/3 right-[8%] hidden sm:block w-[24rem] sm:w-[28rem] h-[24rem] sm:h-[28rem] rounded-full bg-[var(--accent)]/20 blur-[120px] z-0 float-slow" />
      <div className="absolute bottom-[15%] left-[5%] hidden sm:block w-[16rem] sm:w-[20rem] h-[16rem] sm:h-[20rem] rounded-full bg-[var(--accent-2)]/15 blur-[100px] z-0" />

      <div className="relative z-10 mx-auto max-w-7xl px-5 sm:px-8 pb-16 sm:pb-24 w-full">
        <div className="max-w-4xl">
          <div className="tag mb-5 sm:mb-6" data-testid="hero-badge">
            <Sparkles size={12} />
            {heroContent.subtitle || t("hero.badge")}
          </div>

          <h1
            className="font-display font-bold leading-[0.92] tracking-tight"
            style={{ fontSize: "clamp(3rem, 12vw, 9rem)" }}
            data-testid="hero-title"
          >
            {heroHeading.mode === "plain" ? (
              <span className="block text-[var(--text)]">{heroHeading.full}</span>
            ) : (
              <span className="block text-[var(--text)]">
                <span>{heroHeading.brand}</span>
                {" "}
                <span className="accent-text italic font-light">{heroHeading.suffix}</span>
                {heroHeading.tail ? (
                  <>
                    {" "}
                    <span>{heroHeading.tail}</span>
                  </>
                ) : null}
              </span>
            )}
          </h1>

          <p className="mt-6 sm:mt-7 text-base sm:text-xl text-[var(--text-dim)] max-w-xl leading-relaxed whitespace-pre-wrap" data-testid="hero-description">
            {heroContent.description || t("hero.description")}
          </p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.15 }}
            className="mt-7 sm:mt-9 flex flex-wrap items-center gap-2.5 sm:gap-3"
          >
            <button
              type="button"
              onClick={onOpenOrder}
              className="btn-primary inline-flex items-center gap-2"
              data-testid="hero-cta-primary"
            >
              {heroContent.buttonText || t("hero.cta1")}
              <ArrowUpRight size={18} />
            </button>
            <a href="#gallery" className="btn-ghost inline-flex items-center gap-2" data-testid="hero-cta-secondary">
              <Camera size={16} />
              {t("hero.cta2")}
            </a>
            <button
              type="button"
              onClick={onOpenTrainers}
              className="ml-1 inline-flex items-center gap-2 text-sm text-[var(--text-dim)] hover:text-[var(--text)] transition-colors px-2 sm:px-3 py-2 group"
              data-testid="hero-link-trainers"
            >
              <Users size={14} />
              <span className="border-b border-dashed border-[var(--border-strong)] group-hover:border-[var(--accent)] pb-0.5 transition-colors">
                {t("hero.link")}
              </span>
            </button>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.45, delay: 0.25 }}
          className="mt-10 sm:mt-16 flex flex-wrap items-center gap-x-6 sm:gap-x-10 gap-y-3 text-xs sm:text-sm text-[var(--text-mute)]"
        >
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-emerald-400 pulse-glow" />
            <span>{t("hero.indicators.open")}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[var(--text)] tabular-nums">4,6</span>
            <span>· {t("hero.indicators.reviews")}</span>
          </div>
          <div className="hidden sm:flex items-center gap-2">
            <span className="font-semibold text-[var(--text)]">50+</span>
            <span>· {t("hero.indicators.machines")}</span>
          </div>
          <div className="hidden md:flex items-center gap-2">
            <span className="font-semibold text-[var(--text)]">Năvodari</span>
            <span>· {t("hero.indicators.city")}</span>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
